import React from "react";
import { TextNode, View, Image } from "@surge-global-engineering/react-pdf-components";
import { StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import { BookTitleFontMap, PrintExtraProps, BookTitleMeta, getFontFamilyFromFontMap } from "@surge-global-engineering/css-generator";
import {
  themeProperties as preBuildThemeProperties,
  defaultFontMap
} from "../configs";
import {
  PdfChapter,
  PdfExpandedBook,
  PdfTheme,
  PdfThemeProperties,
} from "../types";
import { isPrebuiltTheme } from "../helpers";
import { getTitleChapterData } from "../../../../utils/volumes";

type ThemeHeaderProperties = keyof PdfThemeProperties | "title" | "subtitle" | "author" | "publisher" | "titleImage";
interface TextStylesProperties {
  font: string;
  size: number;
  align: "left" | "center" | "right";
  width: number;
}
const getTextStylesFromProperties = (
  selector: ThemeHeaderProperties,
  baseFontSize: number,
  properties?: PdfThemeProperties | null
) => {
  if (!properties) return {};
  const { font, size, } = properties[
    selector
  ] as TextStylesProperties;
  let fontSize: number | undefined = undefined;

  switch (selector) {
    case "chapterNo":
      fontSize = ((2 + size) / 3) * baseFontSize;
      break;
    case "chapterTitle":
      fontSize = ((4 + size) / 3) * baseFontSize;
      break;
    case "chapterSubtitle":
      fontSize = ((3 + size) / 3) * baseFontSize;
      break;
  }

  const styles = StyleSheet.create({
    text: {
      fontFamily: font,
      fontSize,
      lineHeight: 1 + size / 10,
    },
  });
  return styles.text;
};

const getTextExtraStyles = (
  themeId: string,
  baseFontSize: number,
  selector: ThemeHeaderProperties) => {
  /**
   * ? If theme is a custom one ,
   *  - title card font -> Chapter Title
   *  - publisher details -> Chapter Number
   */

  if (!isPrebuiltTheme(themeId)) return {};
  if (!preBuildThemeProperties[themeId]?.extra) return {};

  if (
    !Object.keys(preBuildThemeProperties[themeId]?.extra || {}).includes(
      selector
    )
  )
    return {};

  const styleProps = (preBuildThemeProperties[themeId]?.extra || {})[
    selector
  ] as PrintExtraProps;

  const style = StyleSheet.create({
    text: {
      ...styleProps,
      paddingTop:
        typeof styleProps.paddingTop === "number"
          ? styleProps.paddingTop * baseFontSize
          : styleProps.paddingTop,
      paddingBottom:
        typeof styleProps.paddingBottom === "number"
          ? styleProps.paddingBottom * baseFontSize
          : styleProps.paddingBottom,
    },
  });

  return style.text;
};


const getDefaultStyles = (
  baseFontSize: number,
  theme: PdfTheme | null
) => {
  return StyleSheet.create({
    publisherText: {
      fontSize: baseFontSize,
      marginTop: 10
    },
    title: {
      fontSize: baseFontSize * 2.5,
      paddingTop: baseFontSize * 0.4,
      textAlign: "center",
      width: "100%",
      fontFamily: theme?.properties?.chapterTitle?.font,
    },
    subtitle: {
      fontSize: baseFontSize * 1.5,
      // paddingTop: baseFontSize * 0.2,
      textAlign: "center",
      width: "100%",
      paddingTop: 20,
      fontFamily: theme?.properties?.chapterTitle?.font,
    },
    author: {
      fontSize: baseFontSize * 1.5,
      // paddingTop: baseFontSize * 0.2,
      textAlign: "center",
      width: "100%",
      paddingTop: 20,
      fontFamily: theme?.properties?.chapterTitle?.font,
    },

  });
};

const getImage = (themeId: string) => {
  if (!isPrebuiltTheme(themeId)) return null;
  const image = preBuildThemeProperties[themeId].extra?.titleImage;
  return image;
};

export interface BookTitleCardProps {
  chapter: PdfChapter;
  theme: PdfTheme;
  book: PdfExpandedBook;
  chapterNumber?: number;
}

const getCustomThemeConstants = (selector: ThemeHeaderProperties) => ({
  // custom theme font sizes were hard coded
  title: {
    fontSize: "33pt",
    paddingTop: 0.6,
    // fontWeight:600
  },

  subtitle: {
    fontSize: "15pt",
    paddingTop: 7,
  },
  author: {
    fontSize: "17pt",
    paddingTop: 20,
  },
})[selector];

const BookTitleCard: React.FC<BookTitleCardProps> = ({
  theme,
  book,
  chapter
}) => {
  const { baseFontSize } = theme.properties;
  const { _id: themeId } = theme;
  const defaultChapterStyles = getDefaultStyles(baseFontSize, theme);
  const { title, subtitle, author, titleType, volumeTitles, publisherLogoURL, publisherName } = getTitleChapterData(book, chapter, "print");
  const publisherLogoExist = publisherLogoURL ? publisherLogoURL.length > 0 : false;
  const hyphenationCallback = (word: string) => [word];
  const image = getImage(themeId);
  
  const getStyleProperties = () => {
    if (theme.isPredefinedTheme)
      return preBuildThemeProperties[themeId] as PdfThemeProperties;
    return theme?.properties;
  };

  const getSelectorStyles = (selector: ThemeHeaderProperties) => {
    const styles = [defaultChapterStyles[selector]];
    const { bookTitlePage, chapterTitle, chapterSubtitle, chapterNo } = theme.properties;
    const pre_selector = bookTitlePage && bookTitlePage[selector] ? bookTitlePage[selector] as BookTitleMeta : null;
      
    if (pre_selector) {
      // get the print extras from the selector
      const headerFonts = {
        chapterTitle: chapterTitle.font,
        chapterNo: chapterNo.font,
        chapterSubtitle: chapterSubtitle.font
      };
      const extras = pre_selector.printExtras || {};
      const font = pre_selector.fontMap ? headerFonts[pre_selector.fontMap] : headerFonts[defaultFontMap[selector]];

      if (extras) {
        styles.push({
          ...extras,
          fontFamily: font,
          paddingTop: typeof extras.paddingTop === "number" ? extras.paddingTop * baseFontSize : extras.paddingTop,
          paddingBottom: typeof extras.paddingBottom === "number" ? extras.paddingBottom * baseFontSize : extras.paddingBottom,
        });
      }
    } else {
      styles.push({
        ...getCustomThemeConstants(selector),
        fontFamily: chapterTitle.font
      });
    }

    return styles;
  };

  return (
    <View style={staticStyles.mainContainer}>
      <View style={staticStyles.topContainer}>
        <View style={staticStyles.titleCard}>
          <TextNode
            hyphenationCallback={hyphenationCallback}
            style={getSelectorStyles("title")}
          >
            {title || ""}
          </TextNode>
          {image && <Image src={image.imageSrc} style={[{ width: image.width }, staticStyles.image]}></Image>}
          <TextNode
            hyphenationCallback={hyphenationCallback}
            style={getSelectorStyles("subtitle")}
          >
            {subtitle || ""}
          </TextNode>
          {titleType === "book" && volumeTitles.map((item, index) =>
            <TextNode key={index}
              style={getSelectorStyles("subtitle")}
            >
              {item.title || ""}
            </TextNode>
          )}
          <TextNode
            hyphenationCallback={hyphenationCallback}
            style={getSelectorStyles("author")}
          >
            {author.join(", ") || ""}
          </TextNode>
        </View>
      </View>
      <View style={staticStyles.bottomContainer}>
        <View style={staticStyles.publisherContainer}>
          {publisherLogoExist && (
            <Image
              style={staticStyles.publisherLogo}
              src={publisherLogoURL}
            />
          )}
          <TextNode
            hyphenationCallback={hyphenationCallback}
            style={[
              defaultChapterStyles.publisherText,
              getTextStylesFromProperties("chapterNo", baseFontSize, getStyleProperties()),
              { fontSize: baseFontSize }
            ]}>
            {publisherName || ""}
          </TextNode>
        </View>
      </View>
    </View>
  );
};

const staticStyles = StyleSheet.create({
  mainContainer: {
    height: "100%",
  },
  topContainer: {
    height: "100%",
  },
  titleCard: {
    top: "20%"
  },
  bottomContainer: {
    position: "absolute",
    bottom: 0,
    width: "100%",
  },
  publisherContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  publisherLogo: {
    alignSelf: "center",
    width: "60pt"
  },
  image: {
    alignSelf: "center"
  }
});

export default BookTitleCard;
